import '@babylonjs/loaders/glTF';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Ray } from '@babylonjs/core/Culling/ray';
import { Space } from '@babylonjs/core/Maths/math.axis';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import type { AnimationGroup } from '@babylonjs/core/Animations/animationGroup';
import type { Bone } from '@babylonjs/core/Bones/bone';
import type { Node } from '@babylonjs/core/node';
import type { Observer } from '@babylonjs/core/Misc/observable';
import type { Scene } from '@babylonjs/core/scene';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import {
  CLIP_PATTERNS,
  ASSET_CONFIGS,
  DEFAULT_ASSET_CONFIG,
  type AssetConfig,
  type ClipRole,
} from '@fisura/shared';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { CreatePlane } from '@babylonjs/core/Meshes/Builders/planeBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { EntityFlag } from '@fisura/protocol';
import type { RenderEntity } from './net.js';
import { buildGunMesh } from './viewmodel.js';

const MODEL_URL = '/models/';
const MODEL_FILE = 'soldier.glb';
const TARGET_HEIGHT = 1.8;
const WALK_SPEED = 2.2;
const RUN_SPEED = 7.0;

let containerPromise: Promise<AssetContainer> | null = null;
let metrics: { scale: number; footLocalY: number } | null = null;
let charCounter = 0;

export function loadHumanoidContainer(scene: Scene): Promise<AssetContainer> {
  if (!containerPromise) {
    containerPromise = SceneLoader.LoadAssetContainerAsync(MODEL_URL, MODEL_FILE, scene);
  }
  return containerPromise;
}

export function assetConfig(): AssetConfig {
  return ASSET_CONFIGS[MODEL_FILE] ?? DEFAULT_ASSET_CONFIG;
}

const clampN = (n: number, lo: number, hi: number): number => (n < lo ? lo : n > hi ? hi : n);

/**
 * Resolve named AnimationGroups to roles using the shared CLIP_PATTERNS, with
 * fallback chains so a model with only idle/walk/run still maps every role.
 */
function resolveClips(groups: AnimationGroup[]): Map<ClipRole, AnimationGroup> {
  const byName = (needles: string[]): AnimationGroup | null => {
    for (const n of needles) {
      const g = groups.find((x) => x.name.toLowerCase().includes(n));
      if (g) return g;
    }
    return null;
  };
  const direct = new Map<ClipRole, AnimationGroup | null>();
  for (const role of Object.keys(CLIP_PATTERNS) as ClipRole[]) {
    direct.set(role, byName(CLIP_PATTERNS[role].match));
  }
  // Follow fallback chains.
  const out = new Map<ClipRole, AnimationGroup>();
  const resolve = (role: ClipRole, depth = 0): AnimationGroup | null => {
    const g = direct.get(role);
    if (g) return g;
    const fb = CLIP_PATTERNS[role].fallback;
    if (fb && depth < 6) return resolve(fb, depth + 1);
    return null;
  };
  for (const role of Object.keys(CLIP_PATTERNS) as ClipRole[]) {
    const g = resolve(role);
    if (g) out.set(role, g);
  }
  return out;
}

/**
 * An animated humanoid instance. Locomotion clips blend by speed; the LEGS/root
 * face the movement direction while the TORSO (spine bone) twists toward the aim
 * yaw and pitches with aim pitch — applied after the animation evaluates each
 * frame. Asset-agnostic via the shared clip-name contract.
 */
export class Character {
  private readonly root: TransformNode;
  private readonly roots: Node[];
  private readonly clips: Map<ClipRole, AnimationGroup>;
  private readonly groups: AnimationGroup[];
  private readonly config: AssetConfig;
  private readonly spine: TransformNode | null;
  private readonly handNode: TransformNode | null;
  private readonly aimObserver: Observer<Scene> | null;

  private torsoYaw = 0;
  private aimPitch = 0;
  private readonly bodyQuat = new Quaternion();
  private readonly scene: Scene;
  private gunHolder: TransformNode | null = null;
  private gunWeaponId = -1;
  private prevProtected = false;
  private readonly gunMetal: StandardMaterial;
  private readonly gunGrip: StandardMaterial;
  private readonly charId: number;
  private lastYaw = 0;
  private deathStart = 0; // > 0 while playing the corpse fall/fade
  private corpseStartY = 0; // root.y at the moment of death
  private corpseFloorY = 0; // root.y the corpse settles at (feet on the floor)
  private smoothY: number | null = null; // smooths stair steps between snapshots
  private nameTag: Mesh | null = null; // floating teammate name (billboard)
  private tagText = '';

  constructor(scene: Scene, container: AssetContainer, teamColor: [number, number, number]) {
    this.scene = scene;
    this.config = assetConfig();
    const inst = container.instantiateModelsToScene((n) => n, true);
    this.root = inst.rootNodes[0] as TransformNode;
    this.roots = inst.rootNodes;
    this.groups = inst.animationGroups;
    this.clips = resolveClips(inst.animationGroups);

    // Scale + foot offset (measured once, reused).
    if (!metrics) {
      let minY = Infinity;
      let maxY = -Infinity;
      for (const m of this.root.getChildMeshes(false)) {
        m.computeWorldMatrix(true);
        const bb = m.getBoundingInfo().boundingBox;
        minY = Math.min(minY, bb.minimumWorld.y);
        maxY = Math.max(maxY, bb.maximumWorld.y);
      }
      const height = maxY - minY || 1;
      metrics = { scale: TARGET_HEIGHT / height, footLocalY: minY };
    }
    this.root.scaling.setAll(metrics.scale);
    // Drive yaw via the quaternion (the glTF import sets root.rotationQuaternion,
    // which makes Euler .rotation assignments a no-op).
    this.root.rotationQuaternion = this.bodyQuat;

    const tint = new Color3(teamColor[0], teamColor[1], teamColor[2]);
    for (const m of this.root.getChildMeshes(false)) {
      const mat = m.material as { albedoColor?: Color3; diffuseColor?: Color3 } | null;
      if (mat?.albedoColor) mat.albedoColor = tint;
      else if (mat?.diffuseColor) mat.diffuseColor = tint;
    }

    // Resolve the spine/chest node for the torso aim override.
    this.spine = this.findSpine(inst.skeletons[0]?.bones);
    // Resolve the right-hand bone so the held weapon tracks the actual hand
    // (otherwise it floats at a fixed offset, untouched by the arm).
    this.handNode = this.findNode(inst.skeletons[0]?.bones, [
      'mixamorig:RightHand', 'RightHand', 'hand_r', 'Hand_R', 'mixamorig:RightHandIndex1',
    ]);

    // Materials for the held third-person weapon (reused across weapon swaps).
    this.gunMetal = new StandardMaterial(`tp-metal-${charCounter}`, scene);
    this.gunMetal.diffuseColor = new Color3(0.16, 0.17, 0.2);
    this.gunMetal.specularColor = new Color3(0.3, 0.3, 0.35);
    this.gunGrip = new StandardMaterial(`tp-grip-${charCounter}`, scene);
    this.gunGrip.diffuseColor = new Color3(0.1, 0.09, 0.08);
    this.charId = charCounter++;

    // All locomotion groups loop continuously; we blend them via weights.
    for (const g of this.groups) {
      g.play(true);
      g.setWeightForAllAnimatables(0);
    }
    this.clips.get('idle')?.setWeightForAllAnimatables(1);

    // Apply the torso/aim override AFTER animations evaluate each frame.
    this.aimObserver = this.spine
      ? scene.onAfterAnimationsObservable.add(() => this.applyAim())
      : null;
  }

  private findSpine(bones: Bone[] | undefined): TransformNode | null {
    return this.findNode(bones, this.config.spineBoneNames);
  }

  /** Resolve a skeleton node by candidate names (exact bone, then descendant). */
  private findNode(bones: Bone[] | undefined, names: string[]): TransformNode | null {
    if (bones) {
      for (const name of names) {
        const b = bones.find((x) => x.name.toLowerCase() === name.toLowerCase());
        const tn = b?.getTransformNode();
        if (tn) return tn;
      }
    }
    for (const n of this.root.getDescendants(false)) {
      const low = n.name.toLowerCase();
      if (names.some((s) => low.includes(s.toLowerCase().replace('mixamorig:', '')))) {
        return n as TransformNode;
      }
    }
    return null;
  }

  update(e: RenderEntity, dt: number): void {
    const scale = metrics?.scale ?? 1;
    const foot = metrics?.footLocalY ?? 0;

    const speed = Math.hypot(e.vx, e.vz);
    const airborne = (e.flags & EntityFlag.OnGround) === 0;
    const crouching = (e.flags & EntityFlag.Crouching) !== 0;

    // Smooth Y while grounded (glide up/down stairs); raw when airborne or
    // teleporting (respawn) so jumps and falls stay crisp.
    if (this.smoothY === null || airborne || Math.abs(e.y - this.smoothY) > 1.5) {
      this.smoothY = e.y;
    } else {
      this.smoothY += (e.y - this.smoothY) * Math.min(1, dt * 14);
    }
    this.root.position.set(e.x, this.smoothY - foot * scale, e.z);

    // The whole body faces the aim yaw 1:1 with the view (plus the asset's
    // fixed forward-axis correction). No movement-direction split / smoothing.
    Quaternion.RotationYawPitchRollToRef(e.yaw + this.config.yawOffset, 0, 0, this.bodyQuat);
    this.root.scaling.y = scale * (crouching ? 0.82 : 1);
    this.lastYaw = e.yaw;

    // Body already faces the aim, so the torso doesn't twist; spine still pitches.
    this.torsoYaw = 0;
    this.aimPitch = clampN(e.pitch, -1.1, 1.1);

    // Blend locomotion weights by role, summing onto shared groups (fallbacks).
    const w = this.weights(speed, airborne, crouching, e.vy);
    const acc = new Map<AnimationGroup, number>();
    for (const role of Object.keys(w) as ClipRole[]) {
      const g = this.clips.get(role);
      if (g) acc.set(g, (acc.get(g) ?? 0) + (w[role] ?? 0));
    }
    for (const g of this.groups) g.setWeightForAllAnimatables(acc.get(g) ?? 0);

    // Held weapon: at shoulder height, pointing where they aim.
    this.syncGun(e);

    // Teammate name floats just above the head (billboarded, depth-tested so
    // walls still hide it — it identifies, it doesn't wallhack).
    this.nameTag?.position.set(e.x, this.smoothY + (crouching ? 1.75 : 2.1), e.z);

    // Spawn protection reads as a translucent "ghost".
    const prot = (e.flags & EntityFlag.Protected) !== 0;
    if (prot !== this.prevProtected) {
      this.prevProtected = prot;
      const v = prot ? 0.4 : 1;
      for (const m of this.root.getChildMeshes(false)) m.visibility = v;
      if (this.gunHolder) for (const m of this.gunHolder.getChildMeshes(false)) m.visibility = v;
    }
  }

  private syncGun(e: RenderEntity): void {
    if (e.weaponId !== this.gunWeaponId) {
      this.gunWeaponId = e.weaponId;
      this.gunHolder?.dispose(false, true);
      this.gunHolder = new TransformNode(`tp-gun-${this.charId}`, this.scene);
      buildGunMesh(this.scene, this.gunHolder, e.weaponId, this.gunMetal, this.gunGrip, `tp-${this.charId}`);
    }
    const g = this.gunHolder!;
    g.rotation.y = e.yaw;
    g.rotation.x = -e.pitch;
    const fwdX = Math.sin(e.yaw);
    const fwdZ = Math.cos(e.yaw);
    if (this.handNode) {
      // Glue the grip to the animated right hand, nudged forward into the palm
      // so the gun reads as held rather than floating.
      this.handNode.computeWorldMatrix(true);
      const p = this.handNode.getAbsolutePosition();
      g.position.set(p.x + fwdX * 0.05, p.y - 0.02, p.z + fwdZ * 0.05);
    } else {
      // Fallback: fixed shoulder offset if the rig has no recognizable hand bone.
      const rightX = Math.cos(e.yaw);
      const rightZ = -Math.sin(e.yaw);
      g.position.set(
        e.x + rightX * 0.22 + fwdX * 0.28,
        e.y + 1.32,
        e.z + rightZ * 0.22 + fwdZ * 0.28,
      );
    }
  }

  private weights(
    speed: number,
    airborne: boolean,
    crouching: boolean,
    vy: number,
  ): Partial<Record<ClipRole, number>> {
    if (airborne) return { [vy > 0.5 ? 'jump' : 'fall']: 1 };
    if (crouching) {
      const t = clampN(speed / WALK_SPEED, 0, 1);
      return { crouch_idle: 1 - t, crouch_walk: t };
    }
    if (speed < WALK_SPEED) {
      const t = speed / WALK_SPEED;
      return { idle: 1 - t, walk_f: t };
    }
    const t = clampN((speed - WALK_SPEED) / (RUN_SPEED - WALK_SPEED), 0, 1);
    return { walk_f: 1 - t, run: t };
  }

  /** Runs after the animation writes the skeleton — composes the aim on top. */
  private applyAim(): void {
    if (!this.spine) return;
    const base = this.spine.rotationQuaternion;
    if (!base) return;
    // Negate pitch: model spine pitches opposite to the camera's aim pitch.
    const add = Quaternion.RotationYawPitchRoll(this.torsoYaw, -this.aimPitch, 0);
    this.spine.rotationQuaternion = base.multiply(add);
    void Space; // (kept for potential bone-space variant)
  }

  /**
   * Show (or update) a floating name above this character's head. Used for
   * teammates only — enemy positions stay earned information.
   */
  setNameTag(text: string, color: [number, number, number]): void {
    if (text === this.tagText) return;
    this.tagText = text;
    this.nameTag?.dispose(false, true);

    const W = 640;
    const H = 160;
    const tex = new DynamicTexture(`tag-tex-${this.charId}`, { width: W, height: H }, this.scene, true);
    tex.hasAlpha = true;
    const ctx = tex.getContext() as unknown as CanvasRenderingContext2D;
    ctx.clearRect(0, 0, W, H);
    // Clean, high-legibility sans-serif (proportional reads better than mono for
    // names), auto-shrunk so long names never clip the plane.
    const sans = '700 #px "Helvetica Neue", Arial, sans-serif';
    let fontPx = 104;
    ctx.font = sans.replace('#', String(fontPx));
    while (ctx.measureText(text).width > W - 48 && fontPx > 36) {
      fontPx -= 4;
      ctx.font = sans.replace('#', String(fontPx));
    }
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.lineJoin = 'round'; // no spiky outline corners
    ctx.lineWidth = Math.round(fontPx * 0.22);
    ctx.strokeStyle = 'rgba(0,0,0,0.9)'; // dark halo so it reads over any background
    ctx.strokeText(text, W / 2, H / 2);
    ctx.fillStyle = `rgb(${Math.round(color[0] * 255)},${Math.round(color[1] * 255)},${Math.round(color[2] * 255)})`;
    ctx.fillText(text, W / 2, H / 2);
    tex.update();

    const mat = new StandardMaterial(`tag-mat-${this.charId}`, this.scene);
    mat.emissiveTexture = tex;
    mat.opacityTexture = tex;
    mat.emissiveColor = Color3.White();
    mat.disableLighting = true;
    // Billboard always faces the camera, so only the front is ever seen. Culling
    // the back face stops its mirror-image from rendering on top (the garbled
    // double-text). Keep alpha blending for the cutout.
    mat.backFaceCulling = true;

    const plane = CreatePlane(`tag-${this.charId}`, { width: 1.6, height: 1.6 * (H / W) }, this.scene);
    plane.material = mat;
    plane.billboardMode = Mesh.BILLBOARDMODE_ALL;
    plane.isPickable = false;
    this.nameTag = plane;
  }

  /** Freeze the rig in its current pose and start the death fall. */
  beginDeath(): void {
    if (this.deathStart) return;
    this.deathStart = performance.now();
    for (const g of this.groups) g.pause(); // hold the pose, no T-pose snap
    if (this.aimObserver) this.aimObserver.remove();
    this.nameTag?.setEnabled(false); // corpses don't need a name floating over them

    // Find the floor beneath the body so a death mid-jump drops to the ground
    // instead of toppling in mid-air. Ray straight down from the feet; the body
    // is still upright here, so it can't hit itself.
    const scale = metrics?.scale ?? 1;
    const foot = metrics?.footLocalY ?? 0;
    this.corpseStartY = this.root.position.y;
    const feetY = this.smoothY ?? this.root.position.y + foot * scale;
    const ray = new Ray(new Vector3(this.root.position.x, feetY + 0.1, this.root.position.z), new Vector3(0, -1, 0), 300);
    const hit = this.scene.pickWithRay(ray, (m) => m.isPickable && m.isEnabled());
    const floorFeetY = hit?.hit && hit.pickedPoint ? hit.pickedPoint.y : feetY;
    this.corpseFloorY = floorFeetY - foot * scale;
  }

  /**
   * Animate the corpse: drop to the floor under gravity (no-op when it died
   * grounded) while tipping over around the feet (~0.4s), then fade out.
   * Returns false once fully faded — caller should dispose.
   */
  updateCorpse(): boolean {
    const t = (performance.now() - this.deathStart) / 1000;
    const tip = Math.min(1, t / 0.4);
    // Ease-in fall: accelerate into the ground like a dropped weight.
    const ang = tip * tip * (Math.PI / 2) * 0.96;
    Quaternion.RotationYawPitchRollToRef(this.lastYaw + this.config.yawOffset, ang, 0, this.bodyQuat);
    // Gravity drop to the floor found at death (g≈22, matching the sim).
    this.root.position.y = Math.max(this.corpseFloorY, this.corpseStartY - 0.5 * 22 * t * t);
    if (t > 1.4) {
      const fade = Math.max(0, 1 - (t - 1.4) / 1.2);
      for (const m of this.root.getChildMeshes(false)) m.visibility = fade;
      if (this.gunHolder) for (const m of this.gunHolder.getChildMeshes(false)) m.visibility = fade;
      if (fade <= 0) return false;
    }
    return true;
  }

  dispose(): void {
    if (this.aimObserver && !this.deathStart) this.aimObserver.remove();
    for (const g of this.groups) g.dispose();
    for (const n of this.roots) n.dispose(false, true);
    this.gunHolder?.dispose(false, true);
    this.nameTag?.dispose(false, true);
    this.gunMetal.dispose();
    this.gunGrip.dispose();
  }
}
